import _ from 'lodash'
import moment from 'moment'
import { search } from '@adc/blueprint'

const { MAXS_BEDS } = search.constants

export const productTypes = {
  resi_bo_v2: 'bank-owned',
  resi_ft: 'foreclosures',
  resi_new_foreclosed: 'newly-foreclosed',
  resi_ip: 'rentals',
  resi_ss: 'short-sales',
  resi_ps: 'private-seller',
}

/**
 * Usage: New asset type (product type) filter route
 * - new route has "_at" added after resi_ft & resi_new_foreclosed
 * - refer to toolkit-adc/src/components/header/constants/route
 */
export const assetTypeFilterRoute = {
  resi_bo_v2: 'bank-owned',
  resi_ft: 'foreclosures_at',
  resi_new_foreclosed: 'newly-foreclosed_at',
  resi_ip: 'rentals',
  resi_ss: 'short-sales_at',
}

export const propertyTypes = {
  resi_sfr: 'single-family',
  resi_mfr: 'multi-family',
  resi_condo: 'condos-townhouses',
  resi_land: 'land',
}

const productDisplayTypes = {
  resi_bo_v2: 'Bank Owned',
  resi_ft: 'Foreclosures',
  resi_ip: 'Rentals',
  resi_ss: 'Short Sales',
}

const propertyDisplayTypes = {
  resi_sfr: 'Single Family',
  resi_mfr: 'Multi-family',
  resi_condo: 'Condo/Townhome',
  resi_land: 'Land',
}

const listingTypes = {
  active: 'Active',
  pending: 'Pending',
  closed: 'Sold',
  canceled: 'Canceled',
}

const assetEventTypes = {
  online: 'Online',
  in_person: 'In Person',
}

const mainDisplay = new Set([
  'State',
  'City',
  'County',
  'Zip',
  'Beds',
  'Baths',
  'Sqft Range',
  'Price Range',
])
const propertyAndAssetTypesSet = new Set(['Property Type', 'Asset Type'])
const listAndAuctionTypesSet = new Set(['Listing Type', 'Auction Type'])
const showOnlySet = new Set([
  'Vacant',
  'Financing Available',
  'Interior Access Available',
  'Reserve Disclosed',
  'Hide Ending Soon',
  'Broker Co-op',
  "No Buyer's Premium",
])
/*
 * Location Parameters
 */
export const showOnlyFields = [
  'y_fa',
  'y_ia',
  'y_sr',
  'y_bc',
  'y_nbp',
  'y_cs',
  'y_hes',
]

export const addUrlKey = (value, filter) => {
  if (showOnlyFields.includes(filter.url)) {
    return filter.url
  } else if (filter.api === 'property_state') {
    return value
  } else if (filter.api === 'property_county') {
    return `${value}-${filter.url}` // dash needed for county
  } else {
    return `${value}_${filter.url}`
  }
}

export const stringSpaceToDash = value => _.join(value.split(' '), '-')

export const auctionFormatConverterUpperCase = value => {
  const auctionArr = value.split(',')
  const auctionLabels = []
  auctionArr.forEach(label => {
    const auctionLabel = assetEventTypes[label]
    if (auctionLabel) auctionLabels.push(auctionLabel)
  })
  return _.join(auctionLabels, ', ')
}

export const booleanStringToBoolean = value => _.toLower(value) === 'y'

export const noBuyerPremiumConverter = value => _.toLower(value) === 'n'

export const propertyTypeConverter = value => {
  const valueArr = value.split(',')
  const apiPropertyTypes = []
  valueArr.forEach(type => {
    const propertyType = propertyTypes[type]
    if (propertyType) apiPropertyTypes.push(propertyType)
  })
  return apiPropertyTypes.join(',')
}

export const propertyTypeConverterWithSpace = value => {
  const valueArr = value.split(',')
  const apiPropertyTypes = []
  valueArr.forEach(type => {
    const propertyType = propertyDisplayTypes[type]
    if (propertyType) apiPropertyTypes.push(propertyType)
  })
  return apiPropertyTypes.join(', ')
}

export const assetTypeConverter = value => {
  const valueArr = value.split(',')
  const apiProductTypes = []
  valueArr.forEach(type => {
    const productType = productTypes[type]
    if (productType) apiProductTypes.push(productType)
  })
  return apiProductTypes.join(',')
}

export const assetTypeConverterWithSpace = value => {
  const valueArr = value.split(',')
  const apiProductTypes = []
  valueArr.forEach(type => {
    const productType = productDisplayTypes[type]
    if (productType) apiProductTypes.push(productType)
  })
  return apiProductTypes.join(', ')
}

export const listingTypeToLabels = value => {
  const valueArr = value.split(',')
  const listingTypelabels = []
  valueArr.forEach((type, i) => {
    const listingType =
      valueArr.length > 2 && i === valueArr.length - 1
        ? `or ${listingTypes[type]}`
        : listingTypes[type]
    if (listingType) listingTypelabels.push(listingType)
  })
  return listingTypelabels.join(', ')
}

export const dateStringToUrl = value => {
  const isDisplay =
    !!value && moment(value).isSame(moment().add(5, 'days'), 'day')
  return isDisplay && `${value}_any`
}

export const sqftObjectToString = value => {
  if (!(value.min || value.max)) return
  return `${value.min ? value.min : 'any'}_${value.max ? value.max : 'any'}`
}

export const sqftToLabel = value => {
  if (!(value.min || value.max)) return
  return `${value.min ? value.min : 'Any'} to ${value.max ? value.max : 'Any'}`
}

export const bedsObjectToString = value => {
  if (typeof value === 'string') return value

  if (!(value.min || value.max)) return

  if (value.min && !value.max) {
    return value.min
  }

  if (value.min && value.max) {
    const result = []
    const minNum = parseInt(value.min)
    const maxNum = parseInt(value.max)

    for (let i = minNum; i <= maxNum; i++) {
      result.push(i)
    }

    return result.join(',')
  }

  if (value.max) {
    return value.max
  }
}

export const bedsToLabel = value => {
  if (!value) return

  if (typeof value === 'string') {
    return `${value}+`
  }

  if (typeof value === 'object' && (value.min || value.max)) {
    const maxBeds = parseInt(value.max)
    if (value.min && value.max && maxBeds !== MAXS_BEDS) {
      return `${value.min} - ${value.max}`
    } else if (value.min && (!value.max || maxBeds === MAXS_BEDS)) {
      return `${value.min}+`
    } else if (value.max) {
      return `Up to ${value.max}`
    }
  }

  return value
}

export const priceToLabel = value => {
  if (!(value.min || value.max)) return
  const formatPrice = price => `$${parseInt(price).toLocaleString()}`
  if (value.min && value.max) {
    return `${formatPrice(value.min)} - ${formatPrice(value.max)}`
  } else if (value.min) {
    return `${formatPrice(value.min)}+`
  } else if (value.max) {
    return `Up to ${formatPrice(value.max)}`
  }
}

export const checkOccupancy = value => _.toLower(value) === 'vacant'

export const numberWithPlus = value => value && `${value}+`

export const categorizeLabels = (labels, inputLabel, paramValue) => {
  if (mainDisplay.has(inputLabel)) {
    labels.mainDisplay.push([inputLabel, paramValue])
  } else if (propertyAndAssetTypesSet.has(inputLabel)) {
    labels.propertyAndAssetTypes.push([inputLabel, paramValue])
  } else if (listAndAuctionTypesSet.has(inputLabel)) {
    labels.listAndAuctionTypes.push([inputLabel, paramValue])
  } else if (showOnlySet.has(inputLabel)) {
    labels.showOnlyFields.push([inputLabel, paramValue])
  }
}

/**
 * Convert asset type search param to filter route part
 */
export const assetTypeRouteConverter = value => {
  const valueArr = value.split(',')
  const apiProductTypes = []
  valueArr.forEach(type => {
    const productType = assetTypeFilterRoute[type]
    if (productType) apiProductTypes.push(productType)
  })
  return apiProductTypes.join(',')
}
